import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mountain, Sun } from 'lucide-react';

const Location = () => {
  return (
    <section
      id="location"
      className="section-container bg-stone-50 relative overflow-hidden"
      data-testid="location-section"
    >
      {/* Desert-themed decorative background */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1504701954957-2010ec3bcec1?q=80&w=2940&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="text-amber-600 font-accent text-xl md:text-2xl mb-4">
            Encuéntranos
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold text-stone-800 tracking-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="location-title"
          >
            Nuestra Ubicación
          </h2>
          <p className="text-lg md:text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Estamos orgullosos de ser parte de la comunidad de Copiapó, en el corazón del norte de Chile.
          </p>
        </motion.div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Map/Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1490750967868-88aa4486c946?q=80&w=2940&auto=format&fit=crop"
                alt="Desierto Florido de Atacama"
                className="w-full h-full object-cover"
                data-testid="location-image"
              />
              {/* Overlay with location pin */}
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent flex items-end justify-center pb-8">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  className="bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 flex items-center gap-3 shadow-lg"
                >
                  <MapPin className="w-6 h-6 text-amber-600" />
                  <span className="font-semibold text-stone-800">Copiapó, Chile</span>
                </motion.div>
              </div>
            </div>
            
            {/* Floating decoration */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-6 -right-6 bg-amber-100 rounded-full p-4 shadow-lg hidden md:block"
            >
              <Sun className="w-8 h-8 text-amber-600" />
            </motion.div>
          </motion.div>

          {/* Info Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-8"
          >
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-amber-100 rounded-2xl">
                  <MapPin className="w-8 h-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-stone-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Copiapó
                  </h3>
                  <p className="text-stone-500">Región de Atacama, Chile</p>
                </div>
              </div>
              
              <p className="text-stone-600 leading-relaxed mb-6">
                Ubicados en la capital de la Región de Atacama, traemos la frescura del desierto florido directamente a tu hogar. Nuestros huevos reflejan la pureza del aire del norte y el cariño de la producción local.
              </p>

              <div className="flex flex-wrap gap-3">
                <span className="bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium">
                  Producción Local
                </span>
                <span className="bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium">
                  Norte de Chile
                </span>
                <span className="bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium">
                  Entrega en Copiapó
                </span>
              </div>
            </div>

            {/* Additional Info Card */}
            <div className="bg-stone-800 text-white rounded-3xl p-8 md:p-10 shadow-lg">
              <div className="flex items-center gap-4 mb-4">
                <Mountain className="w-8 h-8 text-amber-400" />
                <h4 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Del Norte a Tu Mesa
                </h4>
              </div>
              <p className="text-stone-300 leading-relaxed">
                El clima único de Atacama nos permite criar gallinas felices que producen huevos de calidad excepcional. Cada producto lleva el sello del norte de Chile.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Location;
